import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  getDownloadURL,
  getMetadata,
  ref as storageRef,
  uploadBytes,
} from 'firebase/storage';
import { storage } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { AdminTabs } from './VoiceAdmin';

/**
 * Sound library — admin tool for generating, previewing, and saving
 * every sound effect Parrot ships. Four categories:
 *
 *   • Level-ups       — celebration sting played on level promotion.
 *                       One per destination level (Budgie → African Grey).
 *                       Stored at `level-up-sfx/<slug>.mp3` (preserved
 *                       from the original level-up-only version of this
 *                       admin so iOS doesn't need to change paths).
 *   • Score reveals   — short tier-aligned reveals played as the
 *                       numeric score animates in on ScoreRevealView /
 *                       DailyChallengeResultView. Four tiers: elite,
 *                       strong, rising, starting. Stored at
 *                       `score-sfx/<slug>.mp3`.
 *   • Recording       — small UX moments inside the recording loop:
 *                       count-in tick (3-2-1), record-finish chime.
 *                       Stored at `recording-sfx/<slug>.mp3`.
 *   • Brand moments   — app-level identity cues such as the cold-start
 *                       splash. Stored at `brand-sfx/<slug>.mp3`.
 *
 * Per-sound flow is identical across categories: edit the prompt →
 * Generate (calls /api/sfx-generate which proxies ElevenLabs) →
 * preview inline → Save uploads to Firebase Storage.
 */

type SoundCategoryId = 'level-up' | 'score-reveal' | 'recording' | 'brand';

interface SoundDef {
  category: SoundCategoryId;
  /** Within-category slug. Filename = `<slug>.mp3`. */
  slug: string;
  name: string;
  /** Short helper line under the title on the card. */
  hint?: string;
  defaultPrompt: string;
  defaultDuration: number;
  defaultPromptInfluence?: number;
  /** Full Firebase Storage path. Different prefix per category. */
  storagePath: string;
}

interface CategoryMeta {
  id: SoundCategoryId;
  label: string;
  description: string;
}

const CATEGORIES: CategoryMeta[] = [
  {
    id: 'level-up',
    label: 'Level-ups',
    description:
      'Celebration sting played when a user evolves to a new level. One per destination level.',
  },
  {
    id: 'score-reveal',
    label: 'Score reveals',
    description:
      'Short tier-aligned reveal played as the numeric score animates in. Four tiers (Elite, Strong, Rising, Starting) mirror the badge colors on ScoreRevealView.',
  },
  {
    id: 'recording',
    label: 'Recording moments',
    description:
      'Small UX cues inside the recording flow — countdown beat, finish chime.',
  },
  {
    id: 'brand',
    label: 'Brand moments',
    description:
      'App-level identity cues for launch and other polished Parrot moments.',
  },
];

const SOUNDS: SoundDef[] = [
  // ── Level-ups (existing 6 — prompts + storage paths preserved)
  {
    category: 'level-up',
    slug: 'budgie',
    name: 'Budgie',
    hint: 'First level-up — egg hatches',
    defaultPrompt:
      'Cute cartoon egg cracking softly, a delicate chime, then a single happy baby bird chirp. Warm and joyful, no music, clean transients.',
    defaultDuration: 3,
    storagePath: 'level-up-sfx/budgie.mp3',
  },
  {
    category: 'level-up',
    slug: 'parakeet',
    name: 'Parakeet',
    defaultPrompt:
      'Bright cheerful parakeet chirp with a quick ascending magical sparkle behind it. Light, upbeat, no music.',
    defaultDuration: 2.5,
    storagePath: 'level-up-sfx/parakeet.mp3',
  },
  {
    category: 'level-up',
    slug: 'eclectus',
    name: 'Eclectus',
    defaultPrompt:
      'Confident parrot squawk paired with a soft warm drum hit. Punchy, celebratory, no melody.',
    defaultDuration: 3,
    storagePath: 'level-up-sfx/eclectus.mp3',
  },
  {
    category: 'level-up',
    slug: 'hill-myna',
    name: 'Hill Myna',
    defaultPrompt:
      'Mystical resonant bird call with a low warm hum and a gentle gong undertone. Elegant, slightly mysterious, no music.',
    defaultDuration: 3.5,
    storagePath: 'level-up-sfx/hill-myna.mp3',
  },
  {
    category: 'level-up',
    slug: 'yellow-naped-amazon',
    name: 'Yellow-Naped Amazon',
    defaultPrompt:
      'Dramatic vibrant parrot call with a small crowd cheering in the distance and a celebratory chime. Triumphant, no music bed.',
    defaultDuration: 4,
    storagePath: 'level-up-sfx/yellow-naped-amazon.mp3',
  },
  {
    category: 'level-up',
    slug: 'african-grey',
    name: 'African Grey',
    hint: 'Top of the ladder',
    defaultPrompt:
      'Regal trumpet flourish with a deep noble parrot call and a soft reverberant chime. Magisterial, ceremonial, no melody.',
    defaultDuration: 4,
    storagePath: 'level-up-sfx/african-grey.mp3',
  },

  // ── Score reveals (4 tiers, aligned with ScoreRevealView)
  {
    category: 'score-reveal',
    slug: 'elite',
    name: 'Elite (9–10)',
    hint: 'Coral · crown — "Top of the flock"',
    defaultPrompt:
      'Triumphant crown reveal — a quick rising harp sparkle followed by a warm parrot call. Joyful, victorious, no music bed.',
    defaultDuration: 2.5,
    storagePath: 'score-sfx/elite.mp3',
  },
  {
    category: 'score-reveal',
    slug: 'strong',
    name: 'Strong (7–8)',
    hint: 'Success green — "You\'re on the rise"',
    defaultPrompt:
      'Bright affirming chime, like a clean ding, paired with a soft uplift swell. Encouraging, no melody.',
    defaultDuration: 1.8,
    storagePath: 'score-sfx/strong.mp3',
  },
  {
    category: 'score-reveal',
    slug: 'rising',
    name: 'Rising (5–6)',
    hint: 'Blue — "Within striking distance"',
    defaultPrompt:
      'Gentle two-note ascending tone with a soft warm pad behind it. Hopeful, calm, no percussion.',
    defaultDuration: 1.8,
    storagePath: 'score-sfx/rising.mp3',
  },
  {
    category: 'score-reveal',
    slug: 'starting',
    name: 'Starting (0–4)',
    hint: 'Black — "Every parrot starts here"',
    defaultPrompt:
      'Soft single low chime, warm and neutral. Not triumphant, not disappointed — patient and quiet, no music.',
    defaultDuration: 1.5,
    storagePath: 'score-sfx/starting.mp3',
  },

  // ── Recording moments
  {
    category: 'recording',
    slug: 'count-in-tick',
    name: 'Count-in tick',
    hint: 'Played three times during the 3-2-1 countdown',
    defaultPrompt:
      'Single short clean wood-block tick, dry and crisp. No tail, no reverb. Just one transient.',
    defaultDuration: 0.4,
    storagePath: 'recording-sfx/count-in-tick.mp3',
  },
  {
    category: 'recording',
    slug: 'record-finish',
    name: 'Record finish',
    hint: 'Plays when the 60-second timer ends',
    defaultPrompt:
      'Short two-note descending chime, gentle and warm. Reads as "you\'re done" — not alarm, not celebration. No melody beyond the two notes.',
    defaultDuration: 1.2,
    storagePath: 'recording-sfx/record-finish.mp3',
  },

  // ── Brand moments
  {
    category: 'brand',
    slug: 'splash-intro',
    name: 'Splash intro',
    hint: 'Plays once during the cold-start Parrot splash',
    defaultPrompt:
      'Soft musical app intro first: a clear, peaceful instrumental melody, like a warm celesta or music-box tone with a gentle airy pad underneath. No vocals, no human humming, no literal drone hum. No bird sounds during the melody. After the melody finishes, add three tiny single soft chirps, one at a time, like quiet punctuation at the end of a sentence. Never squawky, sharp, loud, or aggressive. End cleanly with a delicate glass bell chime.',
    defaultDuration: 2.8,
    defaultPromptInfluence: 0.78,
    storagePath: 'brand-sfx/splash-intro.mp3',
  },
];

interface PerSoundState {
  prompt: string;
  duration: number;
  promptInfluence: number;
  previewing: boolean;
  saving: boolean;
  playing: boolean;
  error: string | null;
  generatedAt: number | null;
  /// Most recently generated audio buffer — held in memory until the
  /// admin either saves it (uploads to Storage) or generates again.
  pendingAudio: Blob | null;
  /// What's currently uploaded to Storage. `null` = not authored yet.
  remoteURL: string | null;
  remoteUpdatedAt: number | null;
}

const initialState = (sound: SoundDef): PerSoundState => ({
  prompt: sound.defaultPrompt,
  duration: sound.defaultDuration,
  promptInfluence: sound.defaultPromptInfluence ?? 0.4,
  previewing: false,
  saving: false,
  playing: false,
  error: null,
  generatedAt: null,
  pendingAudio: null,
  remoteURL: null,
  remoteUpdatedAt: null,
});

export const SfxAdmin: React.FC = () => {
  const { user, signOutUser } = useAuth();

  const [states, setStates] = useState<Record<string, PerSoundState>>(() => {
    const map: Record<string, PerSoundState> = {};
    for (const sound of SOUNDS) map[sound.slug] = initialState(sound);
    return map;
  });

  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});
  const audioUrlRefs = useRef<Record<string, string | null>>({});

  const update = (slug: string, patch: Partial<PerSoundState>) =>
    setStates((prev) => ({ ...prev, [slug]: { ...prev[slug], ...patch } }));

  // ── Hydrate from Storage on mount so admin can see which sounds are
  //    already authored and which are pending.

  useEffect(() => {
    let cancelled = false;
    // Snapshot the ref's mutable container at effect-mount time. The
    // cleanup below revokes whatever object URLs are tracked here on
    // unmount; capturing the snapshot now keeps the linter happy (the
    // `current` value could change between mount and unmount, and
    // `react-hooks/exhaustive-deps` warns when you read .current
    // directly in the cleanup closure).
    const urlRefSnapshot = audioUrlRefs.current;
    const hydrate = async () => {
      if (!storage) return;
      // Capture to a local const so TS narrows through the `.map`
      // closures — imported bindings stay potentially-null even after
      // a guard, since the import target could be reassigned.
      const s = storage;
      await Promise.all(
        SOUNDS.map(async (sound) => {
          try {
            const ref = storageRef(s, sound.storagePath);
            const [url, meta] = await Promise.all([
              getDownloadURL(ref),
              getMetadata(ref),
            ]);
            if (cancelled) return;
            update(sound.slug, {
              remoteURL: url,
              remoteUpdatedAt: new Date(meta.updated).getTime(),
            });
          } catch {
            // 404 = no sound authored yet — leave remoteURL null.
          }
        })
      );
    };
    hydrate();
    return () => {
      cancelled = true;
      Object.values(urlRefSnapshot).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, []);

  // ── Per-sound handlers

  const stopPlayback = (slug: string) => {
    const audio = audioRefs.current[slug];
    if (audio) {
      audio.pause();
      audio.src = '';
      audioRefs.current[slug] = null;
    }
    const url = audioUrlRefs.current[slug];
    if (url) {
      URL.revokeObjectURL(url);
      audioUrlRefs.current[slug] = null;
    }
    update(slug, { playing: false });
  };

  const handleGenerate = async (slug: string) => {
    const current = states[slug];
    update(slug, { previewing: true, error: null });
    stopPlayback(slug);
    try {
      const resp = await fetch('/api/sfx-generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: current.prompt,
          durationSeconds: current.duration,
          promptInfluence: current.promptInfluence,
        }),
      });
      if (!resp.ok) {
        let message = `Generate failed (HTTP ${resp.status})`;
        try {
          const payload = await resp.json();
          message = payload?.error || message;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      audioUrlRefs.current[slug] = url;
      const audio = new Audio(url);
      audioRefs.current[slug] = audio;
      audio.onplay = () => update(slug, { playing: true });
      audio.onended = () => update(slug, { playing: false });
      audio.onerror = () => update(slug, { playing: false });
      update(slug, {
        pendingAudio: blob,
        generatedAt: Date.now(),
        previewing: false,
      });
      await audio.play();
    } catch (e: any) {
      update(slug, { error: e?.message || 'Generate failed', previewing: false });
    }
  };

  const handlePlay = (slug: string) => {
    const audio = audioRefs.current[slug];
    if (!audio) return;
    audio.currentTime = 0;
    audio.play().catch(() => {});
  };

  const handleSave = async (slug: string) => {
    const current = states[slug];
    const sound = SOUNDS.find((s) => s.slug === slug);
    if (!current.pendingAudio || !storage || !sound) return;
    // Local capture so TS narrows `storage` past the awaits below.
    const s = storage;
    update(slug, { saving: true, error: null });
    try {
      const ref = storageRef(s, sound.storagePath);
      await uploadBytes(ref, current.pendingAudio, { contentType: 'audio/mpeg' });
      const [url, meta] = await Promise.all([
        getDownloadURL(ref),
        getMetadata(ref),
      ]);
      update(slug, {
        saving: false,
        remoteURL: url,
        remoteUpdatedAt: new Date(meta.updated).getTime(),
      });
    } catch (e: any) {
      update(slug, { saving: false, error: e?.message || 'Save failed' });
    }
  };

  // ── Group sounds by category for rendering.

  const soundsByCategory = useMemo(() => {
    const groups: Record<SoundCategoryId, SoundDef[]> = {
      'level-up': [],
      'score-reveal': [],
      recording: [],
      brand: [],
    };
    for (const sound of SOUNDS) groups[sound.category].push(sound);
    return groups;
  }, []);

  // ── UI

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-lg font-bold">Sound library</div>
            <div className="text-xs text-neutral-500">
              Generate, preview, and save every sound effect Parrot ships. Saved files land
              in Firebase Storage and reach the iOS app on next launch.
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-neutral-500 font-mono">{user?.email}</span>
            <button
              onClick={signOutUser}
              className="text-xs text-neutral-600 underline hover:text-neutral-900"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        <AdminTabs current="sfx" />

        {!storage && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm rounded-lg px-4 py-3">
            Firebase Storage isn't configured — see <code>src/lib/firebase.ts</code>. Generate
            previews still work, but Save will be disabled.
          </div>
        )}

        {CATEGORIES.map((category) => (
          <section key={category.id} className="space-y-3">
            <header>
              <div className="text-sm font-bold text-neutral-900 uppercase tracking-wider">
                {category.label}
              </div>
              <div className="text-xs text-neutral-500 mt-0.5">{category.description}</div>
            </header>
            <div className="space-y-3">
              {soundsByCategory[category.id].map((sound) => (
                <SoundCard
                  key={sound.slug}
                  sound={sound}
                  state={states[sound.slug]}
                  onPromptChange={(prompt) => update(sound.slug, { prompt })}
                  onDurationChange={(duration) => update(sound.slug, { duration })}
                  onInfluenceChange={(promptInfluence) =>
                    update(sound.slug, { promptInfluence })
                  }
                  onGenerate={() => handleGenerate(sound.slug)}
                  onPlay={() => handlePlay(sound.slug)}
                  onSave={() => handleSave(sound.slug)}
                  storageReady={!!storage}
                />
              ))}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
};

// ──────────────────────────────────────────────────────────
// Per-sound card

interface SoundCardProps {
  sound: SoundDef;
  state: PerSoundState;
  onPromptChange: (value: string) => void;
  onDurationChange: (value: number) => void;
  onInfluenceChange: (value: number) => void;
  onGenerate: () => void;
  onPlay: () => void;
  onSave: () => void;
  storageReady: boolean;
}

const SoundCard: React.FC<SoundCardProps> = ({
  sound,
  state,
  onPromptChange,
  onDurationChange,
  onInfluenceChange,
  onGenerate,
  onPlay,
  onSave,
  storageReady,
}) => {
  const hasPending = !!state.pendingAudio;
  const hasRemote = !!state.remoteURL;

  const remoteAge = useMemo(() => {
    if (!state.remoteUpdatedAt) return null;
    const minutes = Math.round((Date.now() - state.remoteUpdatedAt) / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    return `${days}d ago`;
  }, [state.remoteUpdatedAt]);

  return (
    <article className="bg-white border border-neutral-200 rounded-xl p-5 space-y-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="text-base font-bold text-neutral-900">{sound.name}</div>
          {sound.hint && <div className="text-xs text-neutral-500 mt-0.5">{sound.hint}</div>}
          <div className="text-[10px] font-mono text-neutral-400 mt-1.5">
            {sound.storagePath}
          </div>
        </div>
        <div className="text-xs text-right shrink-0">
          {hasRemote ? (
            <span className="inline-flex items-center gap-1.5 text-green-700">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
              Authored · {remoteAge}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-neutral-400">
              <span className="w-1.5 h-1.5 rounded-full bg-neutral-300" />
              Not authored
            </span>
          )}
        </div>
      </header>

      <label className="block">
        <div className="text-xs font-medium text-neutral-600 mb-1">ElevenLabs prompt</div>
        <textarea
          rows={3}
          value={state.prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          className="w-full text-sm border border-neutral-200 rounded-lg px-3 py-2 focus:outline-none focus:border-neutral-400 font-mono"
        />
      </label>

      <div className="grid grid-cols-2 gap-4">
        <label className="block">
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-xs font-medium text-neutral-600">Duration</span>
            <span className="text-xs text-neutral-500 font-mono">{state.duration.toFixed(1)}s</span>
          </div>
          <input
            type="range"
            min={0.3}
            max={6}
            step={0.1}
            value={state.duration}
            onChange={(e) => onDurationChange(Number(e.target.value))}
            className="w-full accent-neutral-900"
          />
        </label>
        <label className="block">
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-xs font-medium text-neutral-600">Prompt influence</span>
            <span className="text-xs text-neutral-500 font-mono">
              {state.promptInfluence.toFixed(2)}
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={state.promptInfluence}
            onChange={(e) => onInfluenceChange(Number(e.target.value))}
            className="w-full accent-neutral-900"
          />
        </label>
      </div>

      {state.error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {state.error}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={onGenerate}
          disabled={state.previewing || state.saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 transition disabled:opacity-60"
        >
          {state.previewing ? 'Generating…' : 'Generate'}
        </button>
        {hasPending && (
          <button
            onClick={onPlay}
            disabled={state.playing}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-700 hover:border-neutral-400 transition disabled:opacity-60"
          >
            {state.playing ? 'Playing…' : '▶︎ Replay'}
          </button>
        )}
        {hasRemote && (
          <a
            href={state.remoteURL!}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-700 hover:border-neutral-400 transition"
          >
            ▶︎ Saved
          </a>
        )}
        <div className="ml-auto" />
        <button
          onClick={onSave}
          disabled={!hasPending || state.saving || !storageReady}
          className="px-4 py-2 rounded-lg bg-amber-400 text-neutral-900 text-sm font-bold hover:bg-amber-300 transition disabled:opacity-60"
        >
          {state.saving ? 'Saving…' : hasPending ? 'Save to Storage' : 'Save'}
        </button>
      </div>
    </article>
  );
};
